/**
 * Formik-bound QueryBuilder wrapper for communication rules.
 * Sibling of `Conditions/QueryBuilder/RuleConditionsQueryBuilderFormik` —
 * same skeleton (read `values[fieldId]`, gate on `submitCount`, compute prereqs,
 * call a registry hook, fire `onFieldsLoaded`, render the pure QB) but
 * parameterised over a different first-prereq dim (`eventKey` vs `entity`), a
 * different org-context source (`useOrganization` vs `useSysconfig`), and a
 * different catalog endpoint (rules_engine framework vs assignments legacy).
 *
 * The two wrappers are intentionally kept side-by-side: the shared
 * `useConditionFieldsFetcher` already factors out the catalog-fetch race/digest
 * plumbing, and the remaining ~30 lines of duplication vary along 4 axes that
 * don't compose cleanly into one component. When assignments migrates onto the
 * rules_engine endpoint (registering its own `rule_set_type`), the two will
 * collapse mechanically into a single Formik wrapper.
 *
 * Differences from the assignments sibling:
 *  - Fetches the catalog from the framework endpoint
 *    (`useRuleSetConditionFieldRegistry`) with `rule_set_type='communication_rule'`
 *    and the rule's `event_key` so the BE event-scope filter narrows the
 *    catalog (claim-scope events drop `exposure.*` / `involved_party.*` paths).
 *  - Gates the catalog fetch on event + LOB scope (concrete LOBs or the
 *    all-LOBs flag) + sub-org prerequisites; renders a "Select ..." placeholder
 *    naming the missing dimensions until they're met.
 *
 * The composed pure `RuleConditionsQueryBuilder` writes its tree into Formik
 * under `fieldId` (default `'conditions_json'`).
 */

import React, { useEffect } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';

import useOrganization from '../../../../OrganizationContext';
import { checkConditionPrerequisites } from '../../Conditions/QueryBuilder/prerequisites';
import RuleConditionsQueryBuilder from '../../Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import type { FieldDefinitionDto } from '../../Conditions/QueryBuilder/types';
import { useRuleSetConditionFieldRegistry } from '../../Conditions/QueryBuilder/useRuleSetConditionFieldRegistry';

const COMMUNICATION_RULE_TYPE = 'communication_rule';

interface CommunicationConditionsQueryBuilderFormikProps {
  /** Formik path holding the `conditions_json` tree (default: `'conditions_json'`). */
  fieldId?: string;
  /** Event firing the rule. Empty string = "not yet picked" — the catalog
   *  fetch is gated until the user selects an event (the BE needs it to
   *  filter the catalog by event scope). */
  eventKey: string;
  /** Every LOB the rule is scoped to. Forwarded whole: the catalog is the
   *  cross-LOB intersection, and a rule offered one LOB's fields can be saved
   *  with conditions the other LOBs' claims never carry. */
  lobs: string[];
  /** True when the rule is scoped to every LOB, present and future. Satisfies
   *  the LOB prerequisite on its own and is forwarded as `is_all_lobs`. */
  isAllLobs: boolean;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
  /** Lets the parent form mirror the registry into its own state for use in
   *  Formik's form-level `validate` prop. */
  onFieldsLoaded?: (fields: FieldDefinitionDto[]) => void;
  /** Surfaces the catalog-fetch loading state so the parent dialog can disable
   *  Save while fields are still in flight — mirrors the legacy `Conditions`
   *  control's `setIsLoading` prop. When prereqs aren't met the fetcher is
   *  idle (isLoading=false), so Save behaves normally outside of fetches. */
  onLoadingChange?: (isLoading: boolean) => void;
}

const CommunicationConditionsQueryBuilderFormik: React.FC<CommunicationConditionsQueryBuilderFormikProps> = ({
  fieldId = 'conditions_json',
  eventKey,
  lobs,
  isAllLobs,
  isAllSubOrgs,
  subOrgIds,
  onFieldsLoaded,
  onLoadingChange,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  // Read both `organizationId` and `subOrganizationEnabled` from the same
  // context the parent `AddOrEditRuleDialog` uses, so the prerequisite gate
  // and the parent's `isAllSubOrgs` / `subOrgIds` derivation can't disagree
  // about whether sub-orgs are enabled. `useOrganization` is exported from a
  // JSX file without types; cast at the boundary.
  const { organizationId, subOrganizationEnabled } = useOrganization() as {
    organizationId: number;
    subOrganizationEnabled?: boolean;
  };

  const { prerequisitesMet, prerequisiteMessage } = checkConditionPrerequisites(
    eventKey,
    isAllLobs,
    lobs,
    isAllSubOrgs,
    subOrgIds,
    !!subOrganizationEnabled
  );

  const { fields, isLoading } = useRuleSetConditionFieldRegistry({
    organizationId,
    enabled: prerequisitesMet,
    ruleSetType: COMMUNICATION_RULE_TYPE,
    eventKey,
    lobs,
    isAllLobs,
    isAllSubOrgs,
    subOrgIds,
  });

  // Intentionally only depends on `fields` — `onFieldsLoaded` identity is
  // irrelevant. Including it would silently infinite-loop if a caller passes
  // a non-memoized inline arrow.
  useEffect(() => {
    onFieldsLoaded?.(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  // Same callback-identity caveat as onFieldsLoaded: depend on the boolean
  // value only, not the callback. Parent dialogs typically wire this to a
  // `setIsLoading` setState from useState, whose identity is stable anyway.
  useEffect(() => {
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={prerequisiteMessage}
      emptyMessage="No condition fields available for this event and LOB."
      // A communication rule dispatches on its `event_key`, so it always has an event time.
      hasTriggeringEvent
    />
  );
};

export default CommunicationConditionsQueryBuilderFormik;
